const items = [...document.querySelectorAll('.c-header__secondary-item')];
const triggers = [...document.querySelectorAll('.c-header__secondary-item[aria-expanded]')];

// --- Foldout expand/collapse ---

const toggleFocusable = (foldout, toggle) => {
  tabIndex = toggle ? 0 : -1;
  const links = foldout.querySelectorAll('a');
  for (const link of links) {
    link.tabIndex = tabIndex;
  }
};

const toggleSingleFoldout = (trigger, expand) => {
  // If no transitions are going to be run, waiting for them will result in a promise that never resolves, so we need to check for this.
  const hasTransitions = window.matchMedia("(prefers-reduced-motion: no-preference)").matches;

  // Returning a promise, so that transitions can be run sequentially, rather than simultaneously.
  return new Promise(resolve => {
    const expanded = trigger.getAttribute('aria-expanded') === 'true';
    const control = document.getElementById(trigger.getAttribute('aria-controls'));
    const shouldExpand = expand === undefined ? !expanded : expand;

    // Only hook up a transition event if there's actually anything do to, otherwise the promise will never resolve.
    // If transitions are disabled or don't need to be run, the promise should resolve immediately.
    if (expanded !== shouldExpand) {
      trigger.setAttribute('aria-expanded', shouldExpand.toString());
      control.setAttribute('aria-hidden', !shouldExpand);
      toggleFocusable(control, shouldExpand);

      // Make the promise resolve once transitions have completed, unless transitions have been disabled.
      if (hasTransitions) {
        const handleTransitionEnd = () => {
          resolve();
          control.removeEventListener('transitionend', handleTransitionEnd)
        };
        control.addEventListener('transitionend', handleTransitionEnd);
      }
      else {
        resolve();
      }
    }
    else {
      resolve();
    }
  });
};

const toggleFoldouts = async (trigger, expand) => {
  const otherTriggers = triggers.filter(foldout => foldout !== trigger);

  // Collapse other foldouts first, if any.
  await Promise.all(
    otherTriggers.map(foldout => toggleSingleFoldout(foldout, false))
  );

  // Then expand or collapse the current one.
  await toggleSingleFoldout(trigger, expand);
};

// --- Keyboard navigation stuff ---

const focusFoldout = trigger => {
  const control = document.getElementById(trigger.getAttribute('aria-controls'));
  control?.querySelector('a')?.focus();
};

const findNextItem = (trigger, key) => {
  const index = items.indexOf(trigger);
  const deltas = {
    ArrowLeft: -1,
    ArrowRight: 1,
    Home: -items.length,
    End: items.length,
  };
  if (index === -1 || !deltas[key]) {
    return undefined;
  }
  const newIndex = Math.min(Math.max(index + deltas[key], 0), items.length - 1);
  return items[newIndex];
};

// --- Event handlers ---

const handleClick = async event => {
  event.preventDefault();
  toggleFoldouts(event.currentTarget);
};

const handleKey = async event => {
  if (event.ctrlKey || event.altKey || event.shiftKey || event.metaKey) {
    return;
  }
  const currentItem = event.currentTarget;
  const hasFoldout = triggers.includes(currentItem);
  const isFoldedOut = currentItem.getAttribute('aria-expanded') === 'true';

  // Expand or collapse by up/down keys
  if (hasFoldout && event.key === 'ArrowDown') {
    event.preventDefault();
    await toggleFoldouts(currentItem, true);
    if (isFoldedOut) {
      // If the foldout was already folded out, focus the first link within it
      focusFoldout(currentItem);
    }
    return;
  }
  if (hasFoldout && event.key === 'ArrowUp') {
    event.preventDefault();
    toggleFoldouts(currentItem, false);
    return;
  }

  // Move the focus around by left/right/home/end keys
  const nextTrigger = findNextItem(currentItem, event.key);
  if (nextTrigger) {
    event.preventDefault();
    nextTrigger.focus();
  }
};

const handleDocumentKey = async event => {
  if (event.ctrlKey || event.AltKey || event.shiftKey || event.metaKey) {
    return;
  }

  // Close the currently open foldout, if any, and refocus its trigger
  if (event.key === 'Escape') {
    event.preventDefault();
    const trigger = triggers.find(trigger => trigger.getAttribute('aria-expanded') === 'true');
    if (trigger) {
      await toggleFoldouts(trigger, false);
      trigger.focus();
    }
  }
};

const handleDocumentClick = async event => {
  if (triggers.some(trigger => trigger.contains(event.target))) {
    return;
  }
  const foldout = document.querySelector('.c-header__foldout[aria-hidden=false]');
  if (foldout) {
    const clickedInside = foldout.contains(event.target);
    if (!clickedInside) {
      const trigger = triggers.find(trigger => trigger.getAttribute('aria-controls') === foldout.id);
      toggleFoldouts(trigger, false);
    }
  }
};

// --- Setup ---

triggers.forEach(trigger => {
  trigger.addEventListener('click', handleClick);

  // Foldouts are initially hidden, so we have to make them unfocusable as well
  const control = document.getElementById(trigger.getAttribute('aria-controls'));
  toggleFocusable(control, false);
});

items.forEach(trigger => {
  trigger.addEventListener('keydown', handleKey);
});

document.addEventListener('keydown', handleDocumentKey);
document.addEventListener('click', handleDocumentClick);
